import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { findNearestImageDensityPoint } from '../../utils/imageDensityTooltip';
import { drawImageDensityCanvas } from '../../utils/imageDensityCanvasDraw';

function getTapCoords(event) {
  const touch = event.changedTouches?.[0];
  if (touch) return { clientX: touch.clientX, clientY: touch.clientY };
  return { clientX: event.clientX, clientY: event.clientY };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Canvas interactivo del heatmap (vista principal o mini-gráfico del modal).
 */
export default function ImageDensityInteractiveCanvas({
  points = [],
  selectedPoint = null,
  onSelectPoint,
  interactive = true,
  loadedImageRef,
  imgReady,
  url,
  imageWidth,
  imageHeight,
  renderMode,
  heatmapStyle,
  intensity,
  overlayOpacity,
  blur,
  pointSize,
  separation,
  backgroundDim,
  pointRadius,
  heatmapConfig,
  clusterEnabled,
  clusterRadiusPx,
  clusterShowCount,
  clusterColorByCount,
  hitRadiusMultiplier = 1,
  editMode = false,
  showMarkerLabels = false,
  selectedPositionKey = '',
  markerShape = 'circle',
  markerValueColumn = '',
  markerValueAggregation = 'first',
  markerLabelColumn = '',
  markerFill = 'value',
  markerFit = 'text',
  markerSize = 'medium',
  markerColor = '#ffffff',
  onPlaceAt,
  onDragPoint,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const drawLayoutRef = useRef({ scaleX: 1, scaleY: 1, hitRadius: 14, naturalW: 640, naturalH: 480, points: [] });
  const onSelectRef = useRef(onSelectPoint);
  const onPlaceRef = useRef(onPlaceAt);
  const onDragRef = useRef(onDragPoint);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
  const dragRef = useRef(null);
  onSelectRef.current = onSelectPoint;
  onPlaceRef.current = onPlaceAt;
  onDragRef.current = onDragPoint;

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const draw = (retry = 0) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = loadedImageRef?.current;
      const naturalW = img?.naturalWidth || Number(imageWidth) || 640;
      const naturalH = img?.naturalHeight || Number(imageHeight) || 480;
      const maxW = container.clientWidth || 0;
      const maxH = container.clientHeight || 0;
      if (maxW < 2 || maxH < 2) {
        if (retry < 80) requestAnimationFrame(() => draw(retry + 1));
        return;
      }

      const scale = Math.min(maxW / naturalW, maxH / naturalH);
      const w = Math.max(1, Math.round(naturalW * scale));
      const h = Math.max(1, Math.round(naturalH * scale));
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      drawLayoutRef.current = drawImageDensityCanvas(ctx, w, h, {
        img,
        imgReady,
        url,
        naturalW,
        naturalH,
        points,
        selectedPoint,
        renderMode,
        heatmapStyle,
        intensity,
        overlayOpacity,
        blur,
        pointSize,
        separation,
        backgroundDim,
        pointRadius,
        heatmapConfig,
        clusterEnabled,
        clusterRadiusPx,
        clusterShowCount,
        clusterColorByCount,
        showMarkerLabels,
        editMode,
        selectedPositionKey,
        markerShape,
        markerValueColumn,
        markerValueAggregation,
        markerLabelColumn,
        markerFill,
        markerFit,
        markerSize,
        markerColor,
      });
    };

    draw();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(draw) : null;
    ro?.observe(container);
    window.addEventListener('resize', draw);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', draw);
    };
  }, [
    points,
    selectedPoint,
    imgReady,
    url,
    imageWidth,
    imageHeight,
    renderMode,
    heatmapStyle,
    intensity,
    overlayOpacity,
    blur,
    pointSize,
    separation,
    backgroundDim,
    pointRadius,
    heatmapConfig,
    clusterEnabled,
    clusterRadiusPx,
    clusterShowCount,
    clusterColorByCount,
    loadedImageRef,
    showMarkerLabels,
    editMode,
    selectedPositionKey,
    markerShape,
    markerValueColumn,
    markerValueAggregation,
    markerLabelColumn,
    markerFill,
    markerFit,
    markerSize,
    markerColor,
  ]);

  const coordsFromClient = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    const layout = drawLayoutRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const canvasX = clientX - rect.left;
    const canvasY = clientY - rect.top;
    const scaleX = layout.scaleX || 1;
    const scaleY = layout.scaleY || 1;
    const naturalW = layout.naturalW || Number(imageWidth) || 640;
    const naturalH = layout.naturalH || Number(imageHeight) || 480;
    return {
      canvasX,
      canvasY,
      imageX: clamp(canvasX / scaleX, 0, naturalW),
      imageY: clamp(canvasY / scaleY, 0, naturalH),
    };
  }, [imageWidth, imageHeight]);

  const pickPoint = useCallback(
    (clientX, clientY) => {
      const now = Date.now();
      const last = lastTapRef.current;
      if (
        now - last.time < 400
        && Math.abs(clientX - last.x) < 8
        && Math.abs(clientY - last.y) < 8
      ) {
        return;
      }
      lastTapRef.current = { time: now, x: clientX, y: clientY };

      const layout = drawLayoutRef.current;
      if (!layout.points?.length || !onSelectRef.current) return;
      const coords = coordsFromClient(clientX, clientY);
      if (!coords) return;
      const hit = findNearestImageDensityPoint(
        coords.canvasX,
        coords.canvasY,
        layout.points,
        layout.hitRadius * hitRadiusMultiplier
      );
      if (hit) onSelectRef.current(hit);
    },
    [hitRadiusMultiplier, coordsFromClient]
  );

  const handlePointerDown = useCallback(
    (event) => {
      if (!editMode || event.button !== 0) return;
      const coords = coordsFromClient(event.clientX, event.clientY);
      if (!coords) return;
      const layout = drawLayoutRef.current;
      const hit = findNearestImageDensityPoint(
        coords.canvasX,
        coords.canvasY,
        layout.points,
        Math.max(12, layout.hitRadius * hitRadiusMultiplier)
      );
      const key = hit?.positionKey;
      if (key) {
        dragRef.current = { key, pointerId: event.pointerId, moved: false };
        event.currentTarget?.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      }
    },
    [editMode, coordsFromClient, hitRadiusMultiplier]
  );

  const handlePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!editMode || !drag || drag.pointerId !== event.pointerId) return;
      const coords = coordsFromClient(event.clientX, event.clientY);
      if (!coords) return;
      drag.moved = true;
      onDragRef.current?.({ key: drag.key, x: coords.imageX, y: coords.imageY });
    },
    [editMode, coordsFromClient]
  );

  const handlePointerUp = useCallback(
    (event) => {
      if (event.type === 'pointerup' && event.pointerType === 'mouse' && event.button !== 0) return;
      const drag = dragRef.current;
      if (editMode) {
        if (drag && drag.pointerId === event.pointerId) {
          const coords = coordsFromClient(event.clientX, event.clientY);
          if (coords && drag.moved) {
            onDragRef.current?.({ key: drag.key, x: coords.imageX, y: coords.imageY });
          }
          dragRef.current = null;
          return;
        }
        const coords = coordsFromClient(event.clientX, event.clientY);
        if (coords) onPlaceRef.current?.(coords);
        return;
      }
      if (!interactive) return;
      const { clientX, clientY } = getTapCoords(event);
      pickPoint(clientX, clientY);
    },
    [editMode, interactive, pickPoint, coordsFromClient]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !interactive || editMode || !points.length) return undefined;

    const onTouchEnd = (event) => {
      if (event.changedTouches?.length !== 1) return;
      event.preventDefault();
      const touch = event.changedTouches[0];
      pickPoint(touch.clientX, touch.clientY);
    };

    canvas.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => canvas.removeEventListener('touchend', onTouchEnd);
  }, [interactive, points.length, pickPoint, editMode]);

  const canInteract = interactive || editMode;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        onPointerDown={editMode ? handlePointerDown : undefined}
        onPointerMove={editMode ? handlePointerMove : undefined}
        onPointerUp={canInteract ? handlePointerUp : undefined}
        style={{
          display: 'block',
          borderRadius: 4,
          cursor: editMode ? (selectedPositionKey ? 'crosshair' : 'grab') : interactive && points.length ? 'pointer' : 'default',
          touchAction: editMode ? 'none' : 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      />
    </Box>
  );
}
